import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";
import { IContact } from "../../models/Contact";
import { useQuery } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system";
import { useVirtualizer } from "@tanstack/react-virtual";
import LoadingWheel from "../shared/LoadingWheel";
import apiConfig from "../../utils/apiConfig";

interface ContactComboboxProps {
  onContactSelect: (contact: IContact) => void;
  label?: string;
  selectedContact?: IContact | null;
}

export function SearchContact({ onContactSelect, label, selectedContact }: ContactComboboxProps) {
  const { t } = useTranslation("accounts");
  const { get } = useApiHelper(apiConfig("SearchContact"));
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contacts", "search", debouncedSearch],
    queryFn: () =>
      get<DefaultResponseWithData<IContact[]>>(
        `/contact/search?searchTerm=${debouncedSearch}`
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true,
    enabled: debouncedSearch.length >= 3,
  });

  const contacts = useMemo(() => data?.data ?? [], [data]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: contacts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Increased height to accommodate the new layout
    overscan: 5,
  });

  //Used to prevent scrollIntoView from being called on the options
  useEffect(() => {
    const original = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function () {/* no op */ };
    return () => {
      Element.prototype.scrollIntoView = original;
    };
  }, []);

  return (
    <div className="overflow-visible">
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        by="id"
        value={selectedContact}
        onChange={(contact: IContact) => {
          onContactSelect(contact);
        }}
      >
        <div className="relative mt-1 overflow-visible">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400"
            onChange={(event) => setSearchTerm(event.target.value)}
            displayValue={(contact: IContact) => contact?.fullname}
            placeholder={t("Search by name, email, postcode or account...")}
            autoComplete="nope" //Use non-standard value to prevent browser autocomplete
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {debouncedSearch.length >= 3 && (isLoading || isFetching) ? (
              <div className="flex items-center justify-center h-5 w-5">
                <LoadingWheel />
              </div>
            ) : (
              <ChevronUpDownIcon
                className="size-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions
            ref={parentRef}
            className="absolute z-30 mt-1 max-h-[60vh] w-full overflow-auto bg-gray-100 py-0 text-base rounded-b-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {/* Table-like header */}
            <div className="sticky top-0 z-10 grid grid-cols-6 gap-2 bg-gray-200 text-xs font-semibold text-gray-700 border-b border-gray-300 px-3 py-2">
              <div>{t("Name / Email")}</div>
              <div>{t("Address / Postcode")}</div>
              <div>{t("Phone")}</div>
              <div>{t("Account Name")}</div>
              <div className="flex items-center justify-center h-full">{t("Has Documents")}</div>
              <div className="text-right">{t("Account Status")}</div>
            </div>
            {contacts && contacts.length > 0 && (
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  position: 'relative',
                  width: '100%',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const contact = contacts[virtualRow.index];
                  // Alternate row backgrounds
                  const isEven = virtualRow.index % 2 === 0;
                  const baseBg = isEven ? 'bg-gray-100' : 'bg-gray-200';
                  return (
                    <div
                      key={contact.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`
                      }}
                    >
                      <ComboboxOption
                        key={contact.id}
                        value={contact}
                        className={`group relative cursor-pointer select-none py-2 px-3 text-gray-900 data-[focus]:bg-primary-300 data-[focus]:text-white data-[focus]:outline-none ${baseBg} transition-colors`}
                      >
                        <div className="grid grid-cols-6 gap-2">
                          <div className="col-span-1">
                            <span className="block font-medium">{contact.fullname}</span>
                            <span className="block text-sm text-gray-500">{contact.email || t("No email")}</span>
                          </div>
                          <div className="col-span-1">
                            <span className="block text-sm">{contact.line1}</span>
                            <span className="block text-sm text-gray-500">{contact.postcode || t("No postcode")}</span>
                          </div>
                          <div className="col-span-1">
                            <span className="block text-sm">{contact.telephone1 || t("No phone")}</span>
                          </div>
                          <div className="col-span-1">
                            <span className="block text-sm text-gray-500">{contact.account?.name || t("No account")}</span>
                          </div>
                          <div className="col-span-1 flex items-center justify-center h-full">
                            {contact.hasDocuments ? (
                              <span title={t("Has documents")} className="inline-block w-3 h-3 rounded-full bg-green-500 align-middle" />
                            ) : (
                              <span title={t("No documents")} className="inline-block w-3 h-3 rounded-full bg-red-500 align-middle" />
                            )}
                          </div>
                          <div className="col-span-1 text-right">
                            {contact.account ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t("Has Account")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t("No Account")}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-600 group-data-[selected]:flex group-data-[focus]:text-white">
                          <CheckIcon className="size-5" aria-hidden="true" />
                        </span>
                      </ComboboxOption>
                    </div>
                  );
                })}
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}
